import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import discoveryActions from '../../../../actions/discovery';
import BeforeAfterPreview, { AnimationVariant } from '../../../../components/BeforeAfterPreview';

const PRIMARY = '#FF7E67';
const PLACEHOLDER = 'https://png.pngtree.com/png-vector/20190820/ourmid/pngtree-no-image-vector-illustration-isolated-png-image_1694547.jpg';

// Normalize item to model-like structure
const normalizeItem = (item) => {
  if (item.item_type === 'workflow') {
    return {
      ...item,
      model_id: item.workflow_definition_id,
      model_name: item.name || item.workflow_name,
      model_type: 'workflow',
      model_description: item.description,
      model_cover_image_url: item.workflow_cover_image_url,
      model_cover_2_image_url: item.workflow_cover_2_image_url,
      isWorkflow: true
    };
  }
  return { 
    ...item, 
    model_name: item.model_name || item.name,
    isWorkflow: false 
  };
};

// Get images for an item - handles both raw API items and normalized items
const getItemImages = (item) => {
  const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
  
  if (isWorkflow) {
    const img1 = item.workflow_cover_2_image_url || item.workflow_cover_image_url || item.model_cover_2_image_url || item.model_cover_image_url;
    const img2 = (item.workflow_cover_image_url && item.workflow_cover_image_url !== img1) ? item.workflow_cover_image_url : null;
    return { primary: img1 || PLACEHOLDER, secondary: img2 };
  }
  
  // Model priority: model_cover_2_image_url > model_cover_image_url > model_thumbnail_image_url
  const img1 = item.model_cover_2_image_url || item.model_cover_image_url || item.model_thumbnail_image_url;
  const img2 = (item.model_cover_image_url && item.model_cover_2_image_url && item.model_cover_image_url !== item.model_cover_2_image_url)
    ? item.model_cover_image_url 
    : null;
  return { primary: img1 || PLACEHOLDER, secondary: img2 };
};

/**
 * Desktop modal for model/workflow discovery
 */
export default function ModelSelectorModal({
  isOpen,
  onClose,
  selectedModel,
  onSelectModel,
  formatCost,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(false);
  const [discoveryData, setDiscoveryData] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  // Expanded section state
  const [expandedSection, setExpandedSection] = useState(null);
  const [sectionItems, setSectionItems] = useState([]);
  const [sectionPagination, setSectionPagination] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);

  // Fetch discovery data
  useEffect(() => {
    if (isOpen && !searchQuery) {
      loadDiscoveryData();
    }
  }, [isOpen, activeTab]);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const loadDiscoveryData = async () => {
    setLoading(true);
    setExpandedSection(null);
    try {
      const res = await discoveryActions.getDiscoveryData({ type: activeTab, zenEnabled: true });
      if (res.success) {
        setDiscoveryData(res.data);
      }
    } catch (err) {
      console.error('Failed to load discovery data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load section items with pagination
  const loadSectionItems = async (slug, title, page = 1) => {
    setSectionLoading(true);
    try {
      const res = await discoveryActions.getSectionItems(slug, { type: activeTab, page, limit: 20, zenEnabled: true });
      if (res.success) {
        if (page === 1) {
          setSectionItems(res.data.items);
        } else {
          setSectionItems(prev => [...prev, ...res.data.items]);
        }
        setSectionPagination(res.data.pagination);
        setExpandedSection({ slug, title });
      }
    } catch (err) {
      console.error('Failed to load section items:', err);
    } finally {
      setSectionLoading(false);
    }
  };

  const handleSeeMore = (slug, title) => loadSectionItems(slug, title, 1);

  const handleLoadMore = () => {
    if (expandedSection && sectionPagination?.hasMore) {
      loadSectionItems(expandedSection.slug, expandedSection.title, sectionPagination.page + 1);
    }
  };

  const handleBackToDiscovery = () => {
    setExpandedSection(null);
    setSectionItems([]);
    setSectionPagination(null);
  };

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) {
      setSearchResults(null);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await discoveryActions.searchDiscovery(value, activeTab, true);
        if (res.success && res.data.results) {
          setSearchResults(res.data.results.map(normalizeItem));
        }
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [activeTab]);

  const handleSelect = (item) => {
    const normalized = normalizeItem(item);
    onSelectModel(normalized);
    onClose();
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery('');
    setSearchResults(null);
    setExpandedSection(null);
  };

  // Clear state when closed
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults(null);
      setExpandedSection(null);
    }
  }, [isOpen]);

  // Render search results
  const renderSearchResults = () => {
    if (!searchResults || searchResults.length === 0) {
      return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No results found for "{searchQuery}"</div>;
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, padding: '0 20px' }}>
        {searchResults.map(item => (
          <GridCard key={item.model_id} item={item} isSelected={selectedModel?.model_id === item.model_id} onSelect={() => handleSelect(item)} navigate={navigate} />
        ))}
      </div>
    );
  };

  // Render expanded section
  const renderExpandedSection = () => (
    <div style={{ padding: '0 20px' }}>
      <button onClick={handleBackToDiscovery} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', background: 'none', border: 'none', color: PRIMARY, fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 12 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
        Back
      </button>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 16 }}>
        {expandedSection?.title}
        {sectionPagination && <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 13, marginLeft: 8 }}>({sectionPagination.total} items)</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
        {sectionItems.map(item => {
          const itemId = item.model_id || item.workflow_definition_id;
          return <GridCard key={itemId} item={normalizeItem(item)} isSelected={selectedModel?.model_id === itemId} onSelect={() => handleSelect(item)} navigate={navigate} />;
        })}
      </div>
      {sectionPagination?.hasMore && (
        <button onClick={handleLoadMore} disabled={sectionLoading} style={{ width: '100%', padding: 12, marginTop: 16, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9ca3af', fontSize: 13, cursor: sectionLoading ? 'wait' : 'pointer' }}>
          {sectionLoading ? 'Loading...' : `Load More (${sectionPagination.total - sectionItems.length} remaining)`}
        </button>
      )}
    </div>
  );

  // Render discovery view
  const renderDiscoveryView = () => {
    if (!discoveryData) return null;
    const { trending, recent, categories } = discoveryData;
    const regularCategories = categories?.filter(cat => cat.slug !== 'more-to-explore') || [];
    const moreToExplore = categories?.find(cat => cat.slug === 'more-to-explore');

    return (
      <div>
        {trending?.length > 0 && <HorizontalSection title="🔥 Trending" slug="trending" items={trending} selectedModel={selectedModel} onSelect={handleSelect} onSeeMore={handleSeeMore} navigate={navigate} />}
        {recent?.length > 0 && <HorizontalSection title="✨ Recently Added" slug="recent" items={recent} selectedModel={selectedModel} onSelect={handleSelect} onSeeMore={handleSeeMore} navigate={navigate} />}
        {regularCategories.map(cat => cat.items?.length > 0 && (
          <HorizontalSection key={cat.category_id} title={`${cat.icon || '📁'} ${cat.name}`} slug={cat.slug} items={cat.items} selectedModel={selectedModel} onSelect={handleSelect} onSeeMore={handleSeeMore} navigate={navigate} />
        ))}
        {moreToExplore?.items?.length > 0 && (
          <div style={{ padding: '0 20px' }}>
            <VerticalSection title="🌟 More to Explore" slug="more-to-explore" items={moreToExplore.items} selectedModel={selectedModel} onSelect={handleSelect} onSeeMore={handleSeeMore} navigate={navigate} />
          </div>
        )}
      </div>
    );
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)', zIndex: 9998 }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed',
              inset: 0,
              margin: 'auto',
              width: '90%',
              maxWidth: 900,
              height: 'fit-content',
              maxHeight: '85vh',
              background: '#0f0f0f',
              borderRadius: 16,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#fff' }}>Select Model</h2>
                <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>

              {/* Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search models & workflows..."
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 14 }}
                />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setSearchResults(null); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 8 }}>
                {['all', 'models', 'workflows'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      background: activeTab === tab ? PRIMARY : 'rgba(255,255,255,0.06)',
                      border: activeTab === tab ? `1px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 8,
                      color: activeTab === tab ? '#fff' : '#9ca3af',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
              {loading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ width: 24, height: 24, border: '2px solid rgba(255,255,255,0.1)', borderTopColor: PRIMARY, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                  {searchQuery ? 'Searching...' : 'Loading...'}
                </div>
              ) : searchQuery && searchResults !== null ? (
                renderSearchResults()
              ) : expandedSection ? (
                renderExpandedSection()
              ) : (
                renderDiscoveryView()
              )}
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Horizontal carousel section
function HorizontalSection({ title, slug, items, selectedModel, onSelect, onSeeMore, navigate }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ padding: '0 20px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{title}</span>
        <button onClick={() => onSeeMore(slug, title)} style={{ background: 'none', border: 'none', color: PRIMARY, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>See All →</button>
      </div>
      <div style={{ overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <div style={{ display: 'inline-flex', gap: 12, paddingLeft: 20, paddingRight: 20 }}>
          {items.slice(0, 8).map(item => {
            const itemId = item.model_id || item.workflow_definition_id;
            const images = getItemImages(item);
            const normalized = normalizeItem(item);
            const isWorkflow = item.item_type === 'workflow' || item.isWorkflow;
            return <CarouselCard key={itemId} item={normalized} images={images} isSelected={selectedModel?.model_id === itemId} onSelect={() => onSelect(item)} isWorkflow={isWorkflow} navigate={navigate} />;
          })}
        </div>
      </div>
      <style>{`div::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}

// Carousel card with larger images - BeforeAfterPreview always visible
function CarouselCard({ item, images, isSelected, onSelect, isWorkflow, navigate }) {
  const [isHovered, setIsHovered] = useState(false);
  const hasSlider = images.primary && images.secondary;
  const imgSrc = images.primary || PLACEHOLDER;

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        flexShrink: 0,
        width: 180,
        background: isSelected ? 'rgba(250, 139, 20, 0.12)' : 'rgba(255,255,255,0.04)',
        border: isSelected ? `2px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
        padding: 0,
        transform: isHovered && !isSelected ? 'translateY(-3px)' : 'none',
        boxShadow: isHovered ? '0 12px 28px rgba(0,0,0,0.35)' : 'none',
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: 120, background: '#1a1a1a', overflow: 'hidden' }}>
        {hasSlider ? (
          <BeforeAfterPreview
            before={images.primary}
            after={images.secondary}
            animationVariant={AnimationVariant.SLIDE}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
        ) : (
          <img
            src={imgSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.src = PLACEHOLDER; }}
          />
        )}
        {isWorkflow && (
          <div style={{ position: 'absolute', top: 8, left: 8, fontSize: 10, padding: '3px 6px', borderRadius: 6, background: 'rgba(147, 51, 234, 0.9)', color: '#fff', fontWeight: 600, backdropFilter: 'blur(4px)' }}>Workflow</div>
        )}
        {isSelected ? (
          <div style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 11, background: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const route = isWorkflow
                ? `/workflow/${item.workflow_definition_id || item.model_id}`
                : `/model/${item.model_id}`;
              navigate(route);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: 12,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.15s ease',
            }}
            title="View API docs"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        )}
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ color: isSelected ? PRIMARY : '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.model_name}</div>
      </div>
    </button>
  );
}

// Grid card for search results and expanded view - BeforeAfterPreview always visible
function GridCard({ item, isSelected, onSelect, navigate }) {
  const [isHovered, setIsHovered] = useState(false);
  const images = getItemImages(item);
  const hasSlider = images.primary && images.secondary;
  const isWorkflow = item.isWorkflow || item.item_type === 'workflow';
  const imgSrc = images.primary || PLACEHOLDER;

  return (
    <button
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isSelected ? 'rgba(250, 139, 20, 0.12)' : 'rgba(255,255,255,0.04)',
        border: isSelected ? `2px solid ${PRIMARY}` : '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        textAlign: 'left',
        padding: 0,
        transform: isHovered && !isSelected ? 'translateY(-3px)' : 'none',
        boxShadow: isHovered ? '0 12px 28px rgba(0,0,0,0.35)' : 'none',
      }}
    >
      <div style={{ position: 'relative', width: '100%', height: 120, background: '#1a1a1a', overflow: 'hidden' }}>
        {hasSlider ? (
          <BeforeAfterPreview
            before={images.primary}
            after={images.secondary}
            animationVariant={AnimationVariant.SLIDE}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
        ) : (
          <img
            src={imgSrc}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.src = PLACEHOLDER; }}
          />
        )}
        {isWorkflow && (
          <div style={{ position: 'absolute', top: 8, left: 8, fontSize: 10, padding: '3px 6px', borderRadius: 6, background: 'rgba(147, 51, 234, 0.9)', color: '#fff', fontWeight: 600, backdropFilter: 'blur(4px)' }}>Workflow</div>
        )}
        {isSelected ? (
          <div style={{ position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: 11, background: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
          </div>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const route = isWorkflow
                ? `/workflow/${item.workflow_definition_id || item.model_id}`
                : `/model/${item.model_id}`;
              navigate(route);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: 12,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.15s ease',
            }}
            title="View API docs"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        )}
      </div>
      <div style={{ padding: '10px 12px 12px' }}>
        <div style={{ color: isSelected ? PRIMARY : '#fff', fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.model_name}</div>
      </div>
    </button>
  );
}

// Vertical section for "More to Explore"
function VerticalSection({ title, slug, items, selectedModel, onSelect, onSeeMore, navigate }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{title}</span>
        <button onClick={() => onSeeMore(slug, title)} style={{ background: 'none', border: 'none', color: PRIMARY, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>See All →</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
        {items.slice(0, 6).map(item => {
          const itemId = item.model_id || item.workflow_definition_id;
          return <GridCard key={itemId} item={normalizeItem(item)} isSelected={selectedModel?.model_id === itemId} onSelect={() => onSelect(item)} navigate={navigate} />;
        })}
      </div>
    </div>
  );
}
